/**
 * Ticket list grid — support tickets scoped to a site group or site.
 *
 * Server-backed via EntityGrid over odl-app/tickets/. Scoped to the caller's
 * plant (site group) or structure (site) via extraParams. Staff users get an
 * Edit action column and a "New Ticket" create button (disabled on native
 * platforms). Columns, order, and sort come from the tickets grid-spec
 * descriptor; this file supplies render overrides and the mobile card.
 *
 * Contains:
 * - TicketGrid     — EntityGrid over odl-app/tickets/ (default export)
 * - TicketListItem — mobile single-row card
 */

import { Capacitor } from '@capacitor/core';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { EntityGrid } from '@sophocles/data-grids';
import GridLinkCell from '@sophocles/data-grids/components/cells/GridLinkCell';
import { refLabel } from '@sophocles/features/entities/references';

function fullName(person) {
  if (!person) return '';
  return `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim();
}

function TicketListItem({ row }) {
  return (
    <Box
      component={Link}
      to={`/support/ticket/${row.id}/view`}
      sx={{ display: 'block', textDecoration: 'none', color: 'inherit', py: 1, width: '100%' }}
    >
      <Typography sx={{ fontWeight: 600, color: 'primary.main' }}>{row.ticket_number}</Typography>
      <Typography variant="caption" color="text.secondary">
        {row.site?.name ?? ''}
        {row.issue?.name ? ` — ${row.issue.name}` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {fullName(row.created_by)}
        {' · '}
        {row.created_time ? moment(row.created_time).fromNow() : ''}
      </Typography>
    </Box>
  );
}

export default function TicketGrid({ customerId, siteGroupId, siteId, persistScope = 'ticketGrid' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isStaff } = useUser();

  // Site-context view filters by site; site + site_group columns are redundant there.
  const inSiteContext = Boolean(siteId);

  // Plain keys — toJsonApiParams (in DataGridConfigContext) wraps them as filter[...] on the wire.
  const extraParams = useMemo(
    () => (inSiteContext ? { site_id: siteId } : { site_group_id: siteGroupId }),
    [inSiteContext, siteId, siteGroupId],
  );

  // Render overrides on top of descriptor column defaults. Sizing, order, and
  // headers come from the descriptor; overrides here are intentional display
  // decisions only.
  const fieldOverrides = useMemo(
    () => ({
      ticket_number: {
        cellClassName: 'cell-pinned',
        hideable: false,
        display: 'flex',
        renderCell: ({ row, value }) => (
          <GridLinkCell to={`/support/ticket/${row.id}/view`} sx={{ color: 'primary.main', fontWeight: 500 }}>
            {value}
          </GridLinkCell>
        ),
      },
      created_time: {
        display: 'flex',
        renderCell: ({ value }) =>
          value ? (
            <Tooltip title={moment(value).format('LLL')}>
              <Typography variant="body2">{moment(value).fromNow()}</Typography>
            </Tooltip>
          ) : (
            ''
          ),
      },
      // created_by ships first_name/last_name separately (no top-level name field);
      // join them so the column renders a full name instead of [object Object].
      created_by: {
        display: 'flex',
        valueGetter: (_value, row) => fullName(row?.created_by),
      },
      site: {
        display: 'flex',
        renderCell: ({ row }) =>
          row?.site?.name ? <Chip size="small" label={row.site.name} variant="outlined" /> : null,
      },
      site_group: {
        display: 'flex',
        renderCell: ({ row }) =>
          row?.site_group ? <Chip size="small" label={refLabel(row.site_group)} variant="outlined" /> : null,
      },
      issue: {
        display: 'flex',
        renderCell: ({ row }) =>
          row?.issue?.name ? <Chip size="small" label={row.issue.name} variant="outlined" /> : null,
      },
    }),
    [],
  );

  // Permission-gated Edit column — not modeled in the descriptor.
  const extraColumns = useMemo(() => {
    if (!isStaff) return [];
    return [
      {
        field: 'actions',
        headerName: '',
        width: 96,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        display: 'flex',
        renderCell: ({ row }) => (
          <Button
            size="small"
            onClick={e => {
              e.stopPropagation();
              navigate(`/support/ticket/${row.id}/edit`);
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('actions.edit', 'Edit')}
          </Button>
        ),
      },
    ];
  }, [isStaff, navigate, t]);

  // site and site_group are redundant when the grid is already scoped to that
  // context; id and last_update_time stay hidden via the descriptor's own
  // default_hidden (EntityGrid layers this controlled model over it).
  const columnVisibilityModel = useMemo(() => ({ site: !inSiteContext, site_group: false }), [inSiteContext]);
  const columnVisibility = useMemo(() => ({ model: columnVisibilityModel }), [columnVisibilityModel]);

  const canCreateTicket =
    isStaff && !Capacitor.isNativePlatform() && customerId && siteGroupId && (!inSiteContext || siteId);

  const newTicketPath = inSiteContext
    ? `/c/${customerId}/plant/${siteGroupId}/structure/${siteId}/ticket/new`
    : `/c/${customerId}/plant/${siteGroupId}/ticket/new`;

  const renderMobileCard = useCallback(row => <TicketListItem row={row} />, []);

  return (
    <EntityGrid
      basePath="tickets"
      fieldOverrides={fieldOverrides}
      extraParams={extraParams}
      options={{
        searchPlaceholder: t('general.searchTickets', 'Search tickets'),
        persistKey: persistScope,
        infiniteScroll: true,
        createAction: canCreateTicket
          ? { label: t('general.newTicket', 'New Ticket'), onClick: () => navigate(newTicketPath) }
          : undefined,
        extraColumns,
        mobile: { renderCard: renderMobileCard },
        columnVisibility,
        countNoun: t('support.tickets', 'tickets'),
      }}
    />
  );
}
